import React, { useEffect } from 'react';
import { Button, Col, Form, Input, InputNumber, notification, Row, Select, Space, Typography } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../constants/appSelect';
import { CloseOutlined, ContainerOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getMenuInfo, updateMenu, getAllMenus } from '../../services/menu';

const itemName = 'Menu';
const { Text, Title } = Typography;

const EditMenu = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const { data: itemInfo } = useQuery({ queryKey: ['menuInfo', id], queryFn: () => getMenuInfo(id) });
  const { data: allMenusRes } = useQuery({ queryKey: ['allMenus'], queryFn: getAllMenus, staleTime: 1000 * 60 * 10 });
  const parentOptions = [{ label: '— Không có —', value: 0 }, ...((allMenusRes?.data || []).filter(m => m.id !== Number(id)).map((m) => ({ label: m.title, value: m.id })) || [])];

  useEffect(() => {
    if (itemInfo) {
      form.setFieldsValue({
        title: itemInfo?.title,
        parent_id: itemInfo?.parent_id ?? 0,
        link: itemInfo?.link,
        order: itemInfo?.order ?? 0,
        status: itemInfo?.status,
      });
    }
  }, [form, itemInfo]);

  const { mutate: mutateUpdate, isPending } = useMutation({
    mutationFn: ({ id, payload }) => updateMenu(id, payload),
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Cập nhật ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (data) => {
    const payload = {
      title: data?.title,
      parent_id: Number(data?.parent_id || 0),
      link: data?.link,
      order: Number(data?.order || 0),
      status: data?.status,
    };
    mutateUpdate({ id, payload });
  };

  return (
    <Form name="edit-menu" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Sửa {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="title"
            label={<Text>Tiêu đề <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: `Vui lòng nhập tiêu đề ${itemName}` }]}
          >
            <Input placeholder={`Nhập tiêu đề ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="parent_id" label={<Text>Menu cha</Text>} initialValue={0}>
            <Select
              showSearch
              placeholder="Chọn menu cha"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={parentOptions}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="link" label={<Text>Link</Text>}>
            <Input placeholder={`Nhập đường dẫn menu (VD: /gioi-thieu)`} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="order" label={<Text>Thứ tự</Text>} initialValue={0}>
            <InputNumber min={0} step={1} precision={0} className="w-full" placeholder="Nhập thứ tự (số nguyên)" />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="status"
            label={<Text>Trạng thái <span className="text-red-700">*</span></Text>}
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default EditMenu;
